import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import BatchTradeDialog, { tradeTypeLabel } from "@/components/benchmark/BatchTradeDialog";
import FundPicker from "@/components/benchmark/FundPicker";
import { useRangeFunds } from "@/components/benchmark/rangeFunds";
import { DIST_PORTFOLIOS, distLabel } from "./distConfig";

import { tradeDirectionVariant, tradeDirectionHint } from "@/lib/tradeDirection";
interface Trade {
  id: string; trade_date: string; fund_id: string; direction: string;
  portfolio_1_included: boolean; portfolio_1_pct: number;
  portfolio_2_included: boolean; portfolio_2_pct: number;
  portfolio_3_included: boolean; portfolio_3_pct: number;
  fund_name_override: string | null; notes: string | null; created_at: string; trade_type: string | null; batch_id: string | null;
}
export default function DistTradeLog() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    trade_date: new Date().toISOString().split("T")[0],
    fund_id: "",
    direction: "BUY" as "BUY" | "SELL",
    portfolio_1_included: false, portfolio_1_pct: 0,
    portfolio_2_included: false, portfolio_2_pct: 0,
    portfolio_3_included: false, portfolio_3_pct: 0,
    notes: "",
  });

  const { data: trades } = useQuery({
    queryKey: ["ba-dist-trades"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_dist_trades").select("*").order("trade_date", { ascending: false });
      if (error) throw error;
      return data as Trade[];
    },
  });
  // Selection is resolved from ba_dist_cbam_fund_weights, not from is_current, so a fund sold out
  // of Dist stays re-selectable and can be bought back. See rangeFunds.ts.
  const { selectable: funds, all: allFunds } = useRangeFunds("ba_dist_cbam_fund_weights");

  const addTradeMutation = useMutation({
    mutationFn: async () => {
      if (!form.fund_id) throw new Error("Please select a fund");
      const { error } = await supabase.from("ba_dist_trades").insert(form);
      if (error) throw error;
      // Weight rows carry the TRADE date, not today's — same defect as the P and R single-trade
      // paths: the form captures an editable trade date and writes it to ba_dist_trades, so
      // stamping new Date() here dated the position change differently from its own trade.
      const effectiveDate = form.trade_date;
      const sign = form.direction === "BUY" ? 1 : -1;
      for (const p of DIST_PORTFOLIOS) {
        const included = form[`portfolio_${p}_included` as keyof typeof form] as boolean;
        if (!included) continue;
        const pct = form[`portfolio_${p}_pct` as keyof typeof form] as number;
        // Weight as of the trade date, so a back-dated trade is not computed from its own future.
        const { data: existing } = await supabase.from("ba_dist_cbam_fund_weights").select("weight_pct").eq("fund_id", form.fund_id).eq("portfolio", p).lte("effective_date", effectiveDate).order("effective_date", { ascending: false }).limit(1);
        const current = Number(existing?.[0]?.weight_pct || 0);
        const next = Math.max(0, current + sign * Number(pct || 0));
        await supabase.from("ba_dist_cbam_fund_weights").upsert(
          { fund_id: form.fund_id, portfolio: p, weight_pct: next, effective_date: effectiveDate },
          { onConflict: "fund_id,portfolio,effective_date" }
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-dist-trades"] });
      queryClient.invalidateQueries({ queryKey: ["ba-dist-fund-weights-all"] });
      // Held-status in the picker is derived from the weight rows this trade just wrote.
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-active"] });
      setShowAdd(false);
      toast.success("Trade logged");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Name lookup spans every fund so trades on since-exited funds still render a name.
  const fundMap = new Map(allFunds.map((f) => [f.id, f]));
  const selectedFund = fundMap.get(form.fund_id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dist Trade Log</h1>
          <p className="text-sm text-muted-foreground">Record and review Dist portfolio trades</p>
        </div>
        <div className="flex items-center gap-2">
        <BatchTradeDialog
          tradesTable="ba_dist_trades"
          weightsTable="ba_dist_cbam_fund_weights"
          portfolios={DIST_PORTFOLIOS.map((n) => ({ num: n, label: distLabel(n) }))}
          funds={funds}
          invalidateKeys={[["ba-dist-trades"], ["ba-dist-fund-weights-all"], ["ba-cbam-funds-active"]]}
        />
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> Log Trade</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Log New Trade</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Trade Date</Label><Input type="date" value={form.trade_date} onChange={(e) => setForm({ ...form, trade_date: e.target.value })} /></div>
                <div>
                  <Label>Direction</Label>
                  <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v as "BUY" | "SELL" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="BUY">BUY</SelectItem><SelectItem value="SELL">SELL</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Fund</Label>
                <FundPicker value={form.fund_id} onValueChange={(v) => setForm({ ...form, fund_id: v })} funds={funds} />
                {selectedFund?.previouslyHeldInRange && (
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Previously held in Dist and sold out. A BUY re-opens the position from the trade date.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Portfolio Allocations</Label>
                {DIST_PORTFOLIOS.map((p) => {
                  const inclKey = `portfolio_${p}_included` as keyof typeof form;
                  const pctKey = `portfolio_${p}_pct` as keyof typeof form;
                  return (
                    <div key={p} className="flex items-center gap-3">
                      <Checkbox checked={form[inclKey] as boolean} onCheckedChange={(v) => setForm({ ...form, [inclKey]: !!v })} />
                      <span className="text-sm font-medium w-16">{distLabel(p)}</span>
                      <Input type="number" step="0.01" className="w-24 h-8 text-xs"
                        value={form[pctKey] as number}
                        onChange={(e) => setForm({ ...form, [pctKey]: parseFloat(e.target.value) || 0 })}
                        disabled={!form[inclKey]} />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                  );
                })}
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-20" /></div>
              <Button onClick={() => addTradeMutation.mutate()} disabled={!form.fund_id}>Save Trade</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Type</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund House</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund Class</th>
                <th className="text-left p-3 font-medium text-muted-foreground">ISIN</th>
                <th className="text-center p-3 font-medium text-muted-foreground">Dir</th>
                {DIST_PORTFOLIOS.map((p) => (<th key={p} className="text-center p-3 font-medium text-muted-foreground">{distLabel(p)}</th>))}
                <th className="text-left p-3 font-medium text-muted-foreground">Notes</th>
              </tr>
            </thead>
            <tbody>
              {trades?.map((trade, idx) => {
                const f = fundMap.get(trade.fund_id);
                return (
                  <tr key={trade.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td className="p-3 text-xs">{new Date(trade.trade_date).toLocaleDateString("en-GB")}</td>
                    <td className="p-3 text-xs">{trade.trade_type ? <Badge variant="outline" className="text-[10px]">{tradeTypeLabel(trade.trade_type)}</Badge> : "—"}</td>
                    <td className="p-3 text-xs">{f?.fund_manager || "—"}</td>
                    <td className="p-3 text-xs font-medium">{f?.fund_name || "Unknown"}</td>
                    <td className="p-3 text-xs">{f?.fund_class || "—"}</td>
                    <td className="p-3 text-xs font-mono text-muted-foreground">{f?.isin || "—"}</td>
                    <td className="p-3 text-center"><Badge variant={tradeDirectionVariant(trade.direction)} title={tradeDirectionHint(trade.direction)} className="text-[10px]">{trade.direction}</Badge></td>
                    {DIST_PORTFOLIOS.map((p) => {
                      const incl = trade[`portfolio_${p}_included` as keyof Trade] as boolean;
                      const pct = trade[`portfolio_${p}_pct` as keyof Trade] as number;
                      return (<td key={p} className="p-3 text-center text-xs">{incl ? `${Number(pct).toFixed(2)}%` : "—"}</td>);
                    })}
                    <td className="p-3 text-xs text-muted-foreground max-w-[200px] truncate">{trade.notes || "—"}</td>
                  </tr>
                );
              })}
              {(!trades || trades.length === 0) && (
                <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No trades logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
